import { Box, debounce, Skeleton, Stack, Typography } from '@mui/material';

import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { AutocompleteSearchBar } from 'src/components/SearchBar/autocomplete-search-bar';
import { SideTabStyledWrapper } from 'src/components/SidePanelTabs/SiteTab/site-tab';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { Address, Client, isClientDeleted } from 'src/graphql';
import { DEFAULT_PAGINATION_LIMIT, useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { useClientSiteAssociation } from 'src/pages/Clients/use-client-site-association';
import { SiteCard } from 'src/pages/Conversations/SidePanel/ConversationDetails/ConversationSite/site-card';
import { Modals, useModalMethods } from 'src/store/modals';
import { useInViewTrigger, useScrollPaginateHandler } from 'src/utils/hooks';
import { AppEvents, subscribeToAppEvent } from 'src/utils/hooks/use-app-events';

interface ClientSitesProps {
  client: Client;
}

const SCROLL_THRESHOLD_PX = 80;

const AddressItemSkeleton = () => (
  <Stack
    ml={1}
    overflow="hidden"
    flexGrow={1}
    direction="row"
    spacing={1}
    alignItems="center"
    padding="0.8rem"
  >
    <Skeleton variant="circular" width={20} height={20} />
    <Skeleton variant="circular" width={20} height={20} />
    <Skeleton width="70%" />
  </Stack>
);

export const ClientSites = ({ client }: ClientSitesProps) => {
  const [input, setInput] = useState('');
  const [searchTerm, setSearchTerm] = useState<null | string>('');

  const { showModal: showUpdateClientToAddressModal } = useModalMethods(
    Modals.UpdateClientToAddress,
  );

  const filters = useMemo<ConditionInput[]>(
    () => [
      {
        attribute: 'clients/clientId',
        operator: 'in',
        value: client.clientId,
      },
    ],
    [client.clientId],
  );

  const {
    records: clientAddresses,
    loadMore,
    hasMore,
    isLoading,
    isLoadingMore,
    cacheManager,
  } = useSearch({
    entityName: EntityNames.Site,
    searchTerm: input,
    showResultsWhenEmptySearchTerm: true,
    filter: filters,
  }) ?? {};

  const autocompleteFilters = useMemo(() => {
    const filters = [
      // This filter should be present, since it is also used in multi-tenancy mode
      // when partner-id header is not included in the request.
      {
        attribute: 'partnerId',
        operator: 'in',
        value: [client.partnerId, PLATFORM_PARTNER_ID].join(','),
      },
    ];

    if (clientAddresses?.length) {
      filters.push({
        attribute: 'addressId',
        operator: 'not_in',
        value: clientAddresses.map((site) => site.addressId).join(','),
      });
    }

    return filters;
  }, [client.partnerId, clientAddresses]);

  const {
    records: sitesToAdd,
    loadMore: loadMoreSearch,
    hasMore: hasMoreSearch,
    isLoading: isLoadingSearch,
  } = useSearch({
    entityName: EntityNames.Site,
    searchTerm,
    filter: autocompleteFilters,
  }) ?? {};

  const filteredSitesToAdd = useMemo(
    () =>
      sitesToAdd
        // Remove duplicated elements
        ?.filter(
          (site, index, arr) => arr.findIndex((s) => s.addressId === site.addressId) === index,
        )
        .map((site) => ({
          label: site.fullAddress ?? '',
          ...site,
        })) ?? [],
    [sitesToAdd],
  );

  const inViewElementRef = useInViewTrigger({
    onEnter: loadMore,
    enabled: !isLoading && !!hasMore && !isLoadingMore,
  });

  const handleAutocompleteScroll = useScrollPaginateHandler({
    loadMore: loadMoreSearch,
    enabled: hasMoreSearch,
    thresholdPx: SCROLL_THRESHOLD_PX,
  });

  const { associateSiteToClient } = useClientSiteAssociation(client);

  const { showModal } = useModalMethods(Modals.AddSiteModal);

  const handleOnChange = useMemo(
    () =>
      debounce(({ target }: ChangeEvent) => {
        const value = (target as HTMLInputElement).value;
        setInput(value);
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const onTextFieldChange = useMemo(
    () =>
      debounce(({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchTerm(target.value);
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const onCreateButtonClick = useCallback(() => {
    showModal({
      client: client
        ? {
            partnerId: client.partnerId,
            id: client.clientId,
            name: client.fullName,
            email: client.primaryEmail,
          }
        : undefined,
      filters,
      limit: DEFAULT_PAGINATION_LIMIT,
    });
  }, [client, filters, showModal]);

  const onAutocompleteChange = useCallback(
    async (_: SyntheticEvent, newValue: Partial<Address> | null) => {
      if (newValue) {
        await associateSiteToClient(newValue, filters);
        setSearchTerm('');
      }
    },
    [associateSiteToClient, filters],
  );

  useEffect(() => {
    return subscribeToAppEvent(
      AppEvents.ClientsToAddressAssociation,
      ({ address, clientIds }) => {
        if (!clientIds.includes(client.clientId)) return;
        cacheManager?.update?.((cache) => {
          cache.getAddress.records.push(address as (typeof cache.getAddress.records)[0]);
        });
      },
    );
  }, [cacheManager, client.clientId]);

  const onAddButtonClick = useCallback(() => {
    showUpdateClientToAddressModal({
      client,
    });
  }, [client, showUpdateClientToAddressModal]);

  return (
    <SideTabStyledWrapper>
      <Typography variant="subtitle2" py={1} fontSize="0.75rem">
        Associated Sites
      </Typography>
      <AutocompleteSearchBar
        disabled={isClientDeleted(client)}
        addButtonText="+ Add Site"
        createButtonText="+ Create Site"
        onAddButtonClick={onAddButtonClick}
        searchPlaceholder="Find & Associate Existing Site"
        filteredEntities={filteredSitesToAdd}
        isLoading={isLoadingSearch}
        handleScroll={handleAutocompleteScroll}
        onAutocompleteTextFieldChange={onTextFieldChange}
        onAutocompleteChange={onAutocompleteChange}
        onCreateButtonClick={onCreateButtonClick}
        handleOnFilterResultsChange={handleOnChange}
      />

      <Stack display="grid" gap={3}>
        {!!isLoading && !clientAddresses?.length && <AddressItemSkeleton />}
        {clientAddresses?.map((clientAddress) => (
          <SiteCard key={clientAddress.addressId} address={clientAddress} client={client} />
        ))}
        {!!isLoadingMore && <AddressItemSkeleton />}
        <Box ref={inViewElementRef} sx={{ height: 2 }} />
        {!isLoading && !clientAddresses?.length && (
          <Stack padding="0.5rem">
            <Typography alignSelf="center" variant="body2">
              No results
            </Typography>
          </Stack>
        )}
      </Stack>
    </SideTabStyledWrapper>
  );
};
